import { useEffect, useRef, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, X, Square, Undo2, Eye, ScanEye } from "lucide-react";

// Feature 35 — manual redaction editor. Draw solid rectangles over an owned still image, preview
// the SERVER-rendered burn-in, then save a separate immutable share derivative. Coordinates are
// stored normalized (0..1) so orientation/scaling stays correct server-side.
export function RedactionEditor({ photoId, storagePath, onClose, onSaved }) {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [rects, setRects] = useState([]);
  const [drawing, setDrawing] = useState(null); // {x,y,w,h} in normalized coords
  const [preview, setPreview] = useState(null); // object URL of server preview
  const [busy, setBusy] = useState(false);
  const src = fileUrl(storagePath, 1000);

  const norm = useCallback((clientX, clientY) => {
    const r = imgRef.current.getBoundingClientRect();
    return { x: Math.min(Math.max((clientX - r.left) / r.width, 0), 1),
             y: Math.min(Math.max((clientY - r.top) / r.height, 0), 1) };
  }, []);

  const start = (e) => {
    if (preview) return;
    const pt = "touches" in e ? e.touches[0] : e;
    const p = norm(pt.clientX, pt.clientY);
    setDrawing({ x: p.x, y: p.y, w: 0, h: 0, ox: p.x, oy: p.y });
  };
  const move = (e) => {
    if (!drawing) return;
    const pt = "touches" in e ? e.touches[0] : e;
    const p = norm(pt.clientX, pt.clientY);
    setDrawing((d) => ({ ...d, x: Math.min(d.ox, p.x), y: Math.min(d.oy, p.y),
                         w: Math.abs(p.x - d.ox), h: Math.abs(p.y - d.oy) }));
  };
  const end = () => {
    if (drawing && drawing.w > 0.01 && drawing.h > 0.01) {
      setRects((rs) => [...rs, { x: drawing.x, y: drawing.y, w: drawing.w, h: drawing.h }]);
    }
    setDrawing(null);
  };

  const payload = () => ({ rectangles: rects.map(({ x, y, w, h }) => ({ x, y, w, h })) });

  const doPreview = async () => {
    if (!rects.length) { toast.error("Draw at least one box to redact."); return; }
    setBusy(true);
    try {
      const res = await api.post(`/photos/${photoId}/redactions/preview`, payload(), { responseType: "blob" });
      setPreview(URL.createObjectURL(res.data));
    } catch { toast.error("Couldn't render the preview."); } finally { setBusy(false); }
  };
  const save = async () => {
    if (!rects.length) { toast.error("Draw at least one box to redact."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/photos/${photoId}/redactions`, payload());
      toast.success("Redacted share copy created.");
      onSaved && onSaved(data);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the redacted copy.");
    } finally { setBusy(false); }
  };

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const box = (r, key, live) => (
    <div key={key} style={{ left: `${r.x * 100}%`, top: `${r.y * 100}%`, width: `${r.w * 100}%`, height: `${r.h * 100}%` }}
      className={`absolute ${live ? "bg-black/70 border border-white/70" : "bg-black"}`} />
  );

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-end sm:items-center justify-center" data-testid="redaction-editor" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#E2E8F0]">
          <h4 className="font-heading font-semibold text-navy text-sm flex items-center gap-2"><ScanEye size={16} /> Create a redacted share copy</h4>
          <button data-testid="redaction-close" onClick={onClose} className="text-[#94A3B8]"><X size={18} /></button>
        </div>
        <div className="p-4 overflow-y-auto">
          <p className="text-xs text-[#64748B] mb-3">Drag to draw solid boxes over anything private. The boxes permanently replace those pixels in a separate copy — your original evidence is never changed.</p>
          <div ref={wrapRef} className="relative select-none inline-block w-full">
            {!preview ? (
              <div className="relative touch-none" onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                   onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="redaction-canvas">
                <img ref={imgRef} src={src} alt="" draggable={false} className="w-full rounded-lg border border-[#E2E8F0] pointer-events-none" />
                {rects.map((r, i) => box(r, i, false))}
                {drawing && box(drawing, "live", true)}
              </div>
            ) : (
              <img src={preview} alt="Redacted preview" data-testid="redaction-preview-img" className="w-full rounded-lg border border-[#E2E8F0]" />
            )}
          </div>
        </div>
        <div className="p-4 border-t border-[#E2E8F0] flex flex-wrap gap-2">
          {!preview ? (
            <>
              <Button data-testid="redaction-undo" size="sm" variant="outline" className="rounded-lg" disabled={!rects.length} onClick={() => setRects((rs) => rs.slice(0, -1))}><Undo2 size={14} className="mr-1" /> Undo</Button>
              <span className="text-xs text-[#94A3B8] self-center">{rects.length} box{rects.length === 1 ? "" : "es"}</span>
              <Button data-testid="redaction-preview-btn" size="sm" variant="outline" className="rounded-lg ml-auto" disabled={busy || !rects.length} onClick={doPreview}><Eye size={14} className="mr-1" /> Preview</Button>
              <Button data-testid="redaction-save-btn" size="sm" className="rounded-lg bg-navy text-white" disabled={busy || !rects.length} onClick={save}>{busy ? <Loader2 size={14} className="animate-spin" /> : "Save share copy"}</Button>
            </>
          ) : (
            <>
              <Button data-testid="redaction-back-btn" size="sm" variant="outline" className="rounded-lg" onClick={() => { URL.revokeObjectURL(preview); setPreview(null); }}><Square size={14} className="mr-1" /> Keep editing</Button>
              <Button data-testid="redaction-save-btn" size="sm" className="rounded-lg bg-navy text-white ml-auto" disabled={busy} onClick={save}>{busy ? <Loader2 size={14} className="animate-spin" /> : "Save share copy"}</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
